// Shared contract for a story type's URL segment. A segment is either a legacy
// single word (the type plural, e.g. "recipes") or a template of "/"-delimited
// literal parts and {variable} placeholders (e.g. "{type}/{id}/{slug}").
//
// Variable names are FREE-FORM: "/workflow/{whatever_I_want}" is legal. Four
// names are RESERVED because the URL builder knows how to fill them from the
// story itself; any other name is a custom variable the author binds to the
// story through the page's provides-contexts (source "URI variable"), which
// names both the variable and the field it resolves by.
//
// The PHP twins live in PublicationDynamicComponent::validateStoryTypeUrlTemplate
// and Story::storyTypeUrlTemplateFor; keep the three in lock-step.

const RESERVED_STORY_TYPE_URL_TOKENS = ["slug", "id", "story_id", "type", "title"];

function defaultStoryTypeUrlTemplateFor(storyType) {
  return `${storyType}/{story_id}`;
}

// The story fields a URL variable can resolve a story by: {id} matches the story
// id directly, {slug} costs a custom_url lookup.
const RESOLUTION_KEY_TOKENS = ["slug", "id"];

const LITERAL_PART_PATTERN = /^[A-Za-z0-9_-]+$/;
// Free-form variable names, same character set as a literal path part.
const TOKEN_PART_PATTERN = /^\{([A-Za-z0-9_-]+)\}$/;

// Characters permitted in the input field: template syntax plus literal chars.
// Used to strip only genuinely invalid characters (spaces, punctuation) as typed.
const DISALLOWED_INPUT_CHARS_PATTERN = /[^A-Za-z0-9_/{}-]/g;

const MSG_EMPTY_SEGMENT = "URL template has an empty path segment.";
const MSG_MALFORMED_TOKEN = "Malformed placeholder — write whole variables like {slug}.";
// A template of pure literals addresses one fixed path, so it can never name a
// particular story. At least one variable has to vary.
const MSG_MISSING_VARIABLE = "URL template needs at least one {variable} so it can name a story.";

function duplicateTokenMessage(token) {
  return `Variable {${token}} is used more than once.`;
}

function illegalLiteralMessage(part) {
  return `"${part}" may only contain letters, numbers, hyphens, and underscores.`;
}

function isTemplateSyntax(value) {
  return value.indexOf("{") !== -1 || value.indexOf("}") !== -1 || value.indexOf("/") !== -1;
}

// Strip characters the field can never accept, preserving template syntax.
function sanitizeStoryTypeUrlSegmentInput(value) {
  const text = typeof value === "string" ? value : "";
  return text.replace(DISALLOWED_INPUT_CHARS_PATTERN, "");
}

// Returns { valid: true } or { valid: false, error }. Empty is valid (falls back
// to the default template on build).
function validateStoryTypeUrlTemplate(template) {
  const value = typeof template === "string" ? template.trim() : "";
  if (value === "") {
    return { valid: true };
  }

  if (!isTemplateSyntax(value)) {
    if (!LITERAL_PART_PATTERN.test(value)) {
      return { valid: false, error: illegalLiteralMessage(value) };
    }
    return { valid: true };
  }

  const parts = value.split("/");
  const seenTokens = [];
  for (const part of parts) {
    if (part === "") {
      return { valid: false, error: MSG_EMPTY_SEGMENT };
    }

    const hasBrace = part.indexOf("{") !== -1 || part.indexOf("}") !== -1;
    if (!hasBrace) {
      if (!LITERAL_PART_PATTERN.test(part)) {
        return { valid: false, error: illegalLiteralMessage(part) };
      }
      continue;
    }

    const tokenMatch = part.match(TOKEN_PART_PATTERN);
    if (!tokenMatch) {
      return { valid: false, error: MSG_MALFORMED_TOKEN };
    }

    const token = tokenMatch[1];
    if (seenTokens.includes(token)) {
      return { valid: false, error: duplicateTokenMessage(token) };
    }
    seenTokens.push(token);
  }

  if (seenTokens.length === 0) {
    return { valid: false, error: MSG_MISSING_VARIABLE };
  }

  return { valid: true };
}

// {id} resolves a story directly, {slug} needs a custom_url lookup — prefer the
// former when binding a page's story context to a URL var. Mirrors the order
// Story::resolveStoryLeafFromTemplateCaptures reads captures in.
const PK_BINDING_TOKEN_PREFERENCE = ["id", "story_id", "slug"];

function hasCustomStoryTypeUrlSegment(storyType, segmentMap) {
  const stored = segmentMap?.[storyType];
  return typeof stored === "string" && stored.trim() !== "";
}

// The template a story type's URLs are actually built from. PHP twin:
// Story::storyTypeUrlTemplateFor.
function storyTypeUrlTemplateFor(storyType, segmentMap) {
  if (!storyType) {
    return "";
  }
  const stored = hasCustomStoryTypeUrlSegment(storyType, segmentMap) ? segmentMap[storyType].trim() : "";
  if (stored === "") {
    return defaultStoryTypeUrlTemplateFor(storyType);
  }
  if (stored.indexOf("{") !== -1) {
    return stored;
  }

  return `${stored}/{slug}`;
}

function urlVarsForStoryTypeUrlTemplate(template) {
  return [...String(template ?? "").matchAll(/\{([^}]+)\}/g)].map(match => match[1]);
}

// The token a story-type page's `story` context binds its pk to, "" when the
// template carries none — a custom-named variable has to be bound by hand in the
// inspector, because nothing here can guess that {whatever_I_want} means the id.
function storyTypeUrlPkBindingToken(template) {
  const urlVars = urlVarsForStoryTypeUrlTemplate(template);
  return PK_BINDING_TOKEN_PREFERENCE.find(token => urlVars.includes(token)) ?? "";
}

function isReservedStoryTypeUrlToken(urlVarName) {
  return RESERVED_STORY_TYPE_URL_TOKENS.includes(urlVarName);
}

// Variables the URL builder cannot fill from the story on its own, so they only
// work once bound through the page's provides-contexts.
function customStoryTypeUrlVars(template) {
  return urlVarsForStoryTypeUrlTemplate(template).filter(name => !isReservedStoryTypeUrlToken(name));
}

// Derived, never author-declared: a uri source binds the PK, so a custom name
// holds the id; only reserved `{slug}` means an (invisible) custom_url lookup.
function resolveByForUriBinding(entry) {
  return (entry?.entityPkUrlVars?.id ?? "") === "slug" ? "slug" : "id";
}

// The one variable a story-type URI may carry. A URI-sourced context binds the
// entity PK, so the id token is the only thing the reader can resolve a story by.
const STORY_ID_URL_TOKEN = "story_id";

const DEFAULT_STORY_TYPE_URI = `/story/{${STORY_ID_URL_TOKEN}}`;

const MSG_URI_MISSING_STORY_ID = `URL must contain {${STORY_ID_URL_TOKEN}} — without it the reader cannot tell which story to load.`;

function unmappableUriVarsMessage(names) {
  const list = names.map(name => `{${name}}`).join(", ");
  return `${list} cannot be filled in — remove ${names.length > 1 ? "them" : "it"} or use {${STORY_ID_URL_TOKEN}}.`;
}

// Warnings for a story-type page's URI, in the order they should be shown.
// STRICT by decision: {story_id} is the ONLY permitted variable, so {slug},
// {type} and {title} warn too even though the URL builder could fill them.
function storyTypeUriWarnings(uri) {
  const warnings = [];
  const urlVars = urlVarsForStoryTypeUrlTemplate(String(uri ?? ""));
  if (!urlVars.includes(STORY_ID_URL_TOKEN)) {
    warnings.push(MSG_URI_MISSING_STORY_ID);
  }
  const unmappable = [...new Set(urlVars.filter(name => name !== STORY_ID_URL_TOKEN))];
  if (unmappable.length) {
    warnings.push(unmappableUriVarsMessage(unmappable));
  }
  return warnings;
}

module.exports = {
  DEFAULT_STORY_TYPE_URI,
  STORY_ID_URL_TOKEN,
  storyTypeUriWarnings,
  RESERVED_STORY_TYPE_URL_TOKENS,
  RESOLUTION_KEY_TOKENS,
  customStoryTypeUrlVars,
  defaultStoryTypeUrlTemplateFor,
  hasCustomStoryTypeUrlSegment,
  isReservedStoryTypeUrlToken,
  resolveByForUriBinding,
  sanitizeStoryTypeUrlSegmentInput,
  storyTypeUrlPkBindingToken,
  storyTypeUrlTemplateFor,
  urlVarsForStoryTypeUrlTemplate,
  validateStoryTypeUrlTemplate,
};
